/**
 * Flow snapshot hooks (list/take/restore/delete)
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteFlowSnapshot,
  listFlowSnapshots,
  restoreFlowSnapshot,
  takeFlowSnapshot,
} from '../services/voluum-flow-api';
import type { TrackerId } from '@trafficpulse/shared';
import { invalidateTcRestoredBanks } from '@/features/flow-optimizer/snapshots/restore-cache/tc-restore-cache';

const QK = {
  snapshots: (provider: TrackerId, flowId: string) =>
    [provider, 'flow', flowId, 'snapshots'] as const,
};

export function useFlowSnapshots(
  flowId: string | undefined,
  provider: TrackerId = 'voluum',
) {
  return useQuery({
    queryKey: QK.snapshots(provider, flowId ?? ''),
    queryFn: () => listFlowSnapshots(flowId!, provider),
    enabled: Boolean(flowId),
    staleTime: 10_000,
  });
}

export function useTakeFlowSnapshot(
  flowId: string,
  provider: TrackerId = 'voluum',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label?: string) => takeFlowSnapshot(flowId, { label }, provider),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: QK.snapshots(provider, flowId) }),
  });
}

export function useRestoreFlowSnapshot(
  flowId: string,
  provider: TrackerId = 'voluum',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { snapshotId: string; syncQueue?: boolean }) =>
      restoreFlowSnapshot(flowId, input.snapshotId, provider, input.syncQueue),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.snapshots(provider, flowId) });
      qc.invalidateQueries({ queryKey: [provider, 'flow', 'raw', flowId] });
      qc.invalidateQueries({ queryKey: [provider, 'flow'] });
      qc.invalidateQueries({
        queryKey: [provider, 'flow-optimizer', 'queue'],
      });
      // The restore is recorded as a history run + element events.
      qc.invalidateQueries({
        queryKey: [provider, 'flow-optimizer', 'rotation-history'],
      });
      qc.invalidateQueries({
        queryKey: [provider, 'flow-optimizer', 'queue-events'],
      });
      invalidateTcRestoredBanks(qc, provider);
    },
  });
}

export function useDeleteFlowSnapshot(
  flowId: string,
  provider: TrackerId = 'voluum',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId: string) =>
      deleteFlowSnapshot(flowId, snapshotId, provider),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: QK.snapshots(provider, flowId) }),
  });
}
